import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../Spinner';

export default function FailedLoginsTable() {
  const { token } = useAuth();
  const [failedLogins, setFailedLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [emailFilter, setEmailFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limit, setLimit] = useState(100);

  const fetchFailedLogins = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ limit: limit.toString() });

      if (emailFilter) params.append('email', emailFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await fetch(`${API_URL}/admin/failed-logins?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Errore nel caricamento dei login falliti');
      }

      const data = await response.json();
      setFailedLogins(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, emailFilter, startDate, endDate, limit]);

  useEffect(() => {
    fetchFailedLogins();
  }, [fetchFailedLogins]);

  const getReasonDisplay = (reason) => {
    const reasons = {
      'user_not_found': 'Utente non trovato',
      'invalid_password': 'Password errata',
      'email_not_verified': 'Email non verificata'
    };
    return reasons[reason] || reason;
  };

  const getReasonColor = (reason) => {
    const colors = {
      'user_not_found': 'bg-red-100 text-red-800',
      'invalid_password': 'bg-orange-100 text-orange-800',
      'email_not_verified': 'bg-yellow-100 text-yellow-800'
    };
    return colors[reason] || 'bg-gray-100 text-gray-800';
  };

  if (loading && failedLogins.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="email"
            placeholder="Filtra per email"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            className="px-3 py-2 border rounded"
          />
          <input
            type="date"
            placeholder="Data inizio"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border rounded"
          />
          <input
            type="date"
            placeholder="Data fine"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border rounded"
          />
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-3 py-2 border rounded"
          >
            <option value={50}>Ultimi 50</option>
            <option value={100}>Ultimi 100</option>
            <option value={250}>Ultimi 250</option>
            <option value={500}>Ultimi 500</option>
          </select>
        </div>

        <button
          onClick={fetchFailedLogins}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Caricamento...' : 'Applica filtri'}
        </button>
      </div>

      {/* Table */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Data e ora</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Motivo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Indirizzo IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {failedLogins.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Nessun tentativo di login fallito trovato
                  </td>
                </tr>
              ) : (
                failedLogins.map((login, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      {new Date(login.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{login.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getReasonColor(login.reason)}`}>
                        {getReasonDisplay(login.reason)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{login.ip_address || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-sm text-gray-600 text-center">
        Mostrando {failedLogins.length} tentativi di login falliti
      </div>
    </div>
  );
}
